import { IconButton, TextField, CircularProgress } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { formatCurrency } from 'utils/CurrencyUtils';

import { SelectItems } from './components';

import { useMenuItemFinder } from './hooks';

import { NAMES_OF_SEARCH_FIELDS } from './menuItemFinder.const';
import makeClasses from './MenuItemFinder.style';

import type {
  SelectedItemsOutStock,
  SelectSuggestionsParams,
} from 'features/order/substitution-flow/domain';
import type { OrderId } from 'shared/domain';

export function MenuItemFinder(props: Props) {
  const { orderId, itemsOutStock, onSelectSuggestions } = props;

  const {
    lang,
    itemTitle,
    isFetchingSuggestionWithAI,
    isProduct,
    input1,
    input2,
    handleOnChange,
    handleOnFocus,
    handleOnBlur,
    handleClearTextField,
    handleSelectSuggestion,
  } = useMenuItemFinder({ orderId, itemsOutStock, onSelectSuggestions });

  const _classes = makeClasses();

  return (
    <article
      data-order-id={orderId}
      aria-label='menu-item-finder'
      className={_classes.container}
    >
      <header className={_classes.header}>
        <h2 aria-label='header-title' className={_classes.firstHeading}>
          {lang.header.title}
        </h2>
        <h3 aria-label='item-title'>{itemTitle}</h3>
      </header>

      {isFetchingSuggestionWithAI && (
        <div className={_classes.loaderContainer}>
          <CircularProgress size='3rem' />
        </div>
      )}

      <main className={_classes.main} hidden={isFetchingSuggestionWithAI}>
        <h2 aria-label='main-title' className={_classes.firstHeading}>
          {lang.main.title}
        </h2>
        <section aria-label='finder' className={_classes.finder}>
          <div
            aria-label='field-container'
            className={_classes.searchFieldContainer}
          >
            <TextField
              autoFocus
              fullWidth
              role='search'
              variant='outlined'
              autoComplete='off'
              name={NAMES_OF_SEARCH_FIELDS.first}
              placeholder={lang.input.first.placeholder}
              onChange={handleOnChange}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              inputProps={{ 'data-is-done': input1.isDone, ref: input1.ref }}
              InputProps={{
                classes: { root: _classes.inputBase },
                endAdornment: input1.isDone && (
                  <>
                    {input1.selectedItem && (
                      <span className={_classes.inputPrice}>
                        {formatCurrency(input1.selectedItem.price.real)}
                      </span>
                    )}
                    <IconButton
                      onClick={handleClearTextField}
                      className={_classes.clearTextField}
                      aria-label='clear-field'
                      size='small'
                    >
                      <CancelIcon />
                    </IconButton>
                  </>
                ),
              }}
            />
            {input1.isFocused && (
              <SelectItems
                isProduct={isProduct}
                inputValue={input1.query}
                results={input1.results}
                onSelect={handleSelectSuggestion}
              />
            )}
          </div>

          <div
            aria-label='field-container'
            className={_classes.searchFieldContainer}
          >
            <TextField
              fullWidth
              role='search'
              variant='outlined'
              autoComplete='off'
              name={NAMES_OF_SEARCH_FIELDS.second}
              placeholder={lang.input.second.placeholder}
              onChange={handleOnChange}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              inputProps={{ 'data-is-done': input2.isDone, ref: input2.ref }}
              InputProps={{
                classes: { root: _classes.inputBase },
                endAdornment: input2.isDone && (
                  <>
                    {input2.selectedItem && (
                      <span className={_classes.inputPrice}>
                        {formatCurrency(input2.selectedItem.price.real)}
                      </span>
                    )}
                    <IconButton
                      onClick={handleClearTextField}
                      className={_classes.clearTextField}
                      aria-label='clear-field'
                      size='small'
                    >
                      <CancelIcon />
                    </IconButton>
                  </>
                ),
              }}
            />
            {input2.isFocused && (
              <SelectItems
                isProduct={isProduct}
                inputValue={input2.query}
                results={input2.results}
                onSelect={handleSelectSuggestion}
              />
            )}
          </div>
        </section>
      </main>
    </article>
  );
}

interface Props {
  orderId: OrderId;
  itemsOutStock: SelectedItemsOutStock;
  onSelectSuggestions(params: SelectSuggestionsParams): void;
}
